import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../infrastructure/config/api';
import FounderAccountCard from '../components/founder-account-card';
import TotalBalanceCard from '../components/total-balance-card';

const POLLING_INTERVAL = 30000; // 30 seconds

const FounderSettings = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  // Check authentication and role
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsedUser = JSON.parse(userStr);
      setCurrentUser(parsedUser);
    }
    setIsCheckingAuth(false);
  }, []);

  const fetchData = useCallback(async (isManualRefresh = false) => {
    if (isManualRefresh) {
      console.log('🔄 [FounderSettings] Manual refresh triggered');
      setRefreshing(true);
    } else {
      console.log('📊 [FounderSettings] Auto-fetching founder overview...');
      setLoading(true);
    }
    setError(null);

    try {
      const response = await api.get('/api/account/founder-overview');
      console.log('✅ [FounderSettings] Fetched data:', response.data);

      if (response.data.success && response.data.data) {
        setUsers(response.data.data);
      } else {
        setError('Không thể tải dữ liệu');
        enqueueSnackbar('Không thể tải dữ liệu', { variant: 'error' });
      }
    } catch (err) {
      console.error('❌ [FounderSettings] Error fetching data:', err);
      setError('Lỗi khi tải dữ liệu');
      enqueueSnackbar(
        err.response?.data?.error || 'Lỗi khi tải dữ liệu tổng quan',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [enqueueSnackbar]);

  // Initial fetch and polling
  useEffect(() => {
    if (!currentUser) return;

    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, POLLING_INTERVAL);

    return () => {
      console.log('🧹 [FounderSettings] Cleaning up polling interval');
      clearInterval(interval);
    };
  }, [currentUser, fetchData]);

  const handleRefresh = async (e) => {
    e.stopPropagation();
    await fetchData(true);
  };

  // Check if user is authenticated
  if (isCheckingAuth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Check if user has founder or admin role
  if (!currentUser || (currentUser.role !== 'founder' && currentUser.role !== 'admin')) {
    return <Navigate to="/dashboard" />;
  }

  // Loading skeleton
  if (loading && users.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Error state
  if (error && users.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Founder Settings - Tổng Tài Sản
        </Typography>
        <Tooltip title="Làm mới">
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            color="primary"
            sx={{
              backgroundColor: 'background.paper',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white'
              }
            }}
          >
            <RefreshIcon
              sx={{
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            />
          </IconButton>
        </Tooltip>
      </Box>

      {users.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Không có dữ liệu người dùng
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Tổng số dư tất cả tài khoản */}
          <TotalBalanceCard users={users} />

          {/* Chi tiết từng tài khoản */}
          <Grid container spacing={3}>
            {users.map((user) => (
              <Grid item xs={12} md={6} key={user.user_id}>
                <FounderAccountCard user={user} />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {refreshing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Container>
  );
};

export default FounderSettings;
